import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { classifyError, type Failure } from './errors.ts'

export interface StaleData {
  /** How many queries on screen are showing data their last refresh failed to replace. */
  count: number
  /** When the oldest of that data was fetched. */
  since: Date | null
  /** Why the refresh failed (the first one's failure). */
  failure: Failure | null
  /** Tries every one of them again. */
  retry: () => void
  retrying: boolean
}

/**
 * Data on screen that is older than it looks: queries being watched that
 * hold data but whose last refresh failed. A screen keeps showing what it
 * had (better than a blank page), and the shell says so — "showing what was
 * saved at 3:15 PM" — with Retry, so cached data never passes for current.
 */
/** The watched queries showing data their last refresh failed to replace. */
export function staleQueries(queryClient: QueryClient) {
  return queryClient
    .getQueryCache()
    .getAll()
    .filter(
      (q) =>
        q.getObserversCount() > 0 &&
        q.state.data !== undefined &&
        q.state.status === 'error' &&
        // 401 signs out and 404 is its own screen: neither is "old data".
        ![401, 403, 404].includes(Number((q.state.error as { status?: unknown } | null)?.status)),
    )
}

export function useStaleData(): StaleData {
  const queryClient = useQueryClient()
  const read = () => staleQueries(queryClient)
  const [stale, setStale] = useState(read)
  const [retrying, setRetrying] = useState(false)
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe(() => {
        // The cache talks constantly; re-render only when the stale set changes.
        const next = read()
        setStale((was) =>
          was.length === next.length && was.every((q, i) => q === next[i]) ? was : next,
        )
      }),
    [queryClient],
  )
  const updated = stale.map((q) => q.state.dataUpdatedAt).filter((t) => t > 0)
  return {
    count: stale.length,
    since: updated.length > 0 ? new Date(Math.min(...updated)) : null,
    failure: stale[0] ? classifyError(stale[0].state.error) : null,
    retrying,
    retry: () => {
      setRetrying(true)
      void Promise.allSettled(stale.map((q) => q.fetch())).finally(() => setRetrying(false))
    },
  }
}
